import { useMemo, useEffect, useRef, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND, FORMAT_ELEMENT_COMMAND, ParagraphNode, TextNode } from 'lexical';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { $patchStyleText, $getSelectionStyleValueForProperty } from '@lexical/selection';
import { GOOGLE_FONTS, loadGoogleFont } from './fontUtils';
import { useFontLoader } from './useFontLoader';
import './RichTextEditor.css';

// --- המחלקה המתוקנת ---
export class ExtendedTextNode extends TextNode {
    static getType() {
        return 'extended-text';
    }

    static clone(node) {
        return new ExtendedTextNode(node.__text, node.__key);
    }

    createDOM(config) {
        const dom = super.createDOM(config);
        const style = this.getStyle();
        if (style) dom.style.cssText = style;
        return dom;
    }

    updateDOM(prevNode, dom, config) {
        const isUpdated = super.updateDOM(prevNode, dom, config);
        const style = this.getStyle();
        if (style !== prevNode.getStyle()) {
            dom.style.cssText = style;
            return true;
        }
        return isUpdated;
    }

    static importDOM() {
        const importers = TextNode.importDOM();

        const importWithStyle = (domNode) => {
            // שלב 1: בדיקה אם בכלל יש סטייל שצריך לשמור
            let styleString = domNode.style.cssText;
            if (!styleString) {
                const rawStyle = domNode.getAttribute('style');
                if (rawStyle) styleString = rawStyle;
            }

            // שלב 2: אם אין סטייל מיוחד, אנחנו מחזירים undefined
            // זה קריטי! זה אומר ל-Lexical: "אני לא נוגע בזה, תפרק את התגית הזו לבד"
            // זה מאפשר ל-<b> להיפתח ולהגיע ל-<strong> שבתוכו
            if (!styleString) return undefined;

            // שלב 3: יש סטייל? אנחנו מייצרים את הטקסט עם הצבע
            return {
                conversion: (element) => {
                    const node = new ExtendedTextNode(element.textContent);
                    node.setStyle(styleString);

                    // שימור עיצובים בסיסיים
                    if (['STRONG', 'B'].includes(element.tagName)) node.setFormat(1);
                    if (['EM', 'I'].includes(element.tagName)) node.setFormat(2);
                    if (['U'].includes(element.tagName)) node.setFormat(8);

                    return { node };
                },
                priority: 3 // עדיפות גבוהה מאוד כדי לתפוס את האלמנט הצבעוני
            };
        };

        return {
            ...importers,
            // אנחנו מאזינים רק לתגיות שיכולות להכיל צבע בפוטנציאל
            span: importWithStyle,
            strong: importWithStyle,
            // הערה: הסרתי את b, i, em כדי למנוע התנגשויות בקינון (Nesting)
            // זה יגרום למערכת "לחפור" פנימה עד שתמצא את התגית עם ה-style
        };
    }
}

// --- CustomParagraphNode: כותב line-height על <p> בHTML המיוצא ---
class CustomParagraphNode extends ParagraphNode {
    static getType() {
        return 'custom-paragraph';
    }

    static clone(node) {
        return new CustomParagraphNode(node.__key);
    }

    createDOM(config) {
        const dom = super.createDOM(config);
        const lh = this._getLineHeight();
        if (lh) dom.style.lineHeight = lh;
        return dom;
    }

    updateDOM(prevNode, dom, config) {
        super.updateDOM(prevNode, dom, config);
        const lh = this._getLineHeight();
        const prevLh = typeof prevNode._getLineHeight === 'function' ? prevNode._getLineHeight() : null;
        if (lh !== prevLh) {
            dom.style.lineHeight = lh || '';
        }
        return false;
    }

    exportDOM(editor) {
        const result = super.exportDOM(editor);
        const lh = this._getLineHeight();
        if (lh && result.element) {
            result.element.style.lineHeight = lh;
        }
        return result;
    }

    static importDOM() {
        return {
            p: () => ({
                conversion: (element) => {
                    const node = new CustomParagraphNode();
                    if (element.style?.textAlign) node.setFormat(element.style.textAlign);
                    const lh = element.style?.lineHeight;
                    if (lh) node.setStyle(`line-height: ${lh}`);
                    return { node };
                },
                priority: 1,
            }),
        };
    }

    insertNewAfter(rangeSelection, restoreSelection) {
        const newElement = super.insertNewAfter(rangeSelection, restoreSelection);
        if (newElement && typeof newElement.setStyle === 'function') {
            const style = this.getStyle();
            if (style) newElement.setStyle(style);
        }
        return newElement;
    }

    _getLineHeight() {
        const style = this.getStyle() || '';
        const match = style.match(/line-height:\s*([^;]+)/);
        return match ? match[1].trim() : null;
    }
}

// --- פלאגינים ---

function InitialHTMLPlugin({ html }) {
    const [editor] = useLexicalComposerContext();
    const isInitialized = useRef(false);

    useEffect(() => {
        if (!isInitialized.current && html) {
            isInitialized.current = true;
            editor.update(() => {
                const parser = new DOMParser();
                const dom = parser.parseFromString(html, 'text/html');
                const nodes = $generateNodesFromDOM(editor, dom);
                const root = $getRoot();
                root.clear();
                root.append(...nodes);
            });
        }
    }, [editor, html]);

    return null;
}

const FONT_SIZES = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '40px', '48px', '60px', '72px', '84px', '96px', '108px', '120px', '144px', '160px', '180px'];
const LINE_HEIGHTS = ['1', '1.2', '1.4', '1.5', '1.6', '1.8', '2', '2.5', '3'];

function ToolbarPlugin({ scale = 1 }) {
    const [editor] = useLexicalComposerContext();
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [fontSize, setFontSize] = useState('16px');
    const [fontColor, setFontColor] = useState('#000000');
    const [fontFamily, setFontFamily] = useState('inherit');
    const [lineHeight, setLineHeight] = useState('1');

    // טעינת כל הפונטים מראש לתצוגה מקדימה ב-dropdown
    useEffect(() => {
        GOOGLE_FONTS.forEach(font => {
            if (font.value !== 'inherit') {
                loadGoogleFont(font.value);
            }
        });
    }, []);

    const formatText = (format) => editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
    const formatAlignment = (alignment) => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignment);

    const onFontSizeChange = (e) => {
        const displaySize = e.target.value; // הגודל שהמשתמש רואה
        const numericSize = parseFloat(displaySize);
        const actualSize = `${numericSize / scale}px`; // הגודל האמיתי שנשמר ב-HTML

        setFontSize(displaySize);
        editor.update(() => {
            const selection = $getSelection();
            if (selection) $patchStyleText(selection, { 'font-size': actualSize });
        });
    };

    const onFontColorChange = (e) => {
        const newColor = e.target.value;
        setFontColor(newColor);
        editor.update(() => {
            const selection = $getSelection();
            if (selection) $patchStyleText(selection, { 'color': newColor });
        });
    };

    const onLineHeightChange = (e) => {
        const newLineHeight = e.target.value;
        setLineHeight(newLineHeight);
        editor.update(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return;

            // 1. עדכון ה-paragraph node (לייצוא HTML נכון עם <p style="line-height:...">)
            const paragraphs = new Set();
            selection.getNodes().forEach(node => {
                const parent = node.getParent();
                if (parent && typeof parent.setStyle === 'function') paragraphs.add(parent);
            });
            if (paragraphs.size === 0) {
                const parent = selection.anchor.getNode().getParent();
                if (parent && typeof parent.setStyle === 'function') paragraphs.add(parent);
            }
            paragraphs.forEach(paragraph => {
                const existing = paragraph.getStyle() || '';
                const newStyle = existing
                    .split(';')
                    .map(s => s.trim())
                    .filter(s => s && !s.startsWith('line-height'))
                    .concat(`line-height: ${newLineHeight}`)
                    .join('; ');
                paragraph.setStyle(newStyle);
            });

            // 2. עדכון text nodes (כדי שהעורך יציג את המרווח בזמן עריכה)
            $patchStyleText(selection, { 'line-height': newLineHeight });
        });
    };

    const onFontFamilyChange = (e) => {
        const newFont = e.target.value;
        setFontFamily(newFont);

        // טעינת הפונט מ-Google Fonts
        loadGoogleFont(newFont);

        editor.update(() => {
            const selection = $getSelection();
            if (selection) {
                const fontValue = newFont === 'inherit' ? 'inherit' : `"${newFont}", sans-serif`;
                $patchStyleText(selection, { 'font-family': fontValue });
            }
        });
    };

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                    setIsBold(selection.hasFormat('bold'));
                    setIsItalic(selection.hasFormat('italic'));
                    setIsUnderline(selection.hasFormat('underline'));

                    // קריאת הגודל האמיתי מה-HTML והמרה לגודל תצוגה
                    const actualSize = $getSelectionStyleValueForProperty(selection, 'font-size', '16px');
                    const numericSize = parseFloat(actualSize);
                    const displaySize = `${Math.round(numericSize * scale)}px`;
                    setFontSize(displaySize);

                    setFontColor($getSelectionStyleValueForProperty(selection, 'color', '#000000'));

                    // קריאת הפונט הנוכחי
                    const currentFont = $getSelectionStyleValueForProperty(selection, 'font-family', 'inherit');
                    // ניקוי מירכאות ו-fallback fonts
                    const cleanFont = currentFont.replace(/["']/g, '').split(',')[0].trim();
                    setFontFamily(cleanFont === 'inherit' || cleanFont === '' ? 'inherit' : cleanFont);

                    // קריאת מרווח שורות — קודם מה-paragraph, אחרת מה-text node
                    const anchorParent = selection.anchor.getNode().getParent();
                    const pStyle = anchorParent && typeof anchorParent.getStyle === 'function'
                        ? anchorParent.getStyle() || ''
                        : '';
                    const pLhMatch = pStyle.match(/line-height:\s*([^;]+)/);
                    if (pLhMatch) {
                        setLineHeight(pLhMatch[1].trim());
                    } else {
                        const textLh = $getSelectionStyleValueForProperty(selection, 'line-height', '1');
                        setLineHeight(textLh || '1');
                    }
                }
            });
        });
    }, [editor, scale]);

    return (
        <div className="rich-text-toolbar">
            <div className="rich-text-toolbar__group">
                <select
                    onChange={onFontFamilyChange}
                    value={fontFamily}
                    className="rich-text-toolbar__select rich-text-toolbar__select--font"
                    style={{ fontFamily: fontFamily !== 'inherit' ? `"${fontFamily}", sans-serif` : 'inherit' }}
                >
                    {GOOGLE_FONTS.map(font => (
                        <option
                            key={font.value}
                            value={font.value}
                            style={{ fontFamily: font.value !== 'inherit' ? `"${font.value}", sans-serif` : 'inherit' }}
                        >
                            {font.name}
                        </option>
                    ))}
                </select>
                <select onChange={onFontSizeChange} value={fontSize} className="rich-text-toolbar__select">
                    {FONT_SIZES.map(size => <option key={size} value={size}>{size}</option>)}
                </select>
                <div className="rich-text-toolbar__line-height-wrapper" title="מרווח שורות">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" style={{ flexShrink: 0 }}>
                        <rect x="4" y="0" width="10" height="1.5" rx="0.75"/>
                        <rect x="4" y="6" width="10" height="1.5" rx="0.75"/>
                        <rect x="4" y="12" width="10" height="1.5" rx="0.75"/>
                        <path d="M1.5 2.5 L1.5 5 L0.5 4 M1.5 5 L2.5 4" stroke="currentColor" strokeWidth="1" fill="none"/>
                        <path d="M1.5 11.5 L1.5 9 L0.5 10 M1.5 9 L2.5 10" stroke="currentColor" strokeWidth="1" fill="none"/>
                    </svg>
                    <select
                        onChange={onLineHeightChange}
                        value={lineHeight}
                        className="rich-text-toolbar__select rich-text-toolbar__select--line-height"
                    >
                        {LINE_HEIGHTS.map(lh => <option key={lh} value={lh}>{lh}</option>)}
                    </select>
                </div>
                <div className="rich-text-toolbar__color-wrapper">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '16px', lineHeight: '1', color: '#000' }}>A</span>
                        <div style={{ width: '100%', height: '3px', backgroundColor: fontColor, borderRadius: '1px' }}></div>
                    </div>
                    <input type="color" onChange={onFontColorChange} value={fontColor} className="rich-text-toolbar__color-input" />
                </div>
            </div>
            <div className="rich-text-toolbar__separator"></div>
            <div className="rich-text-toolbar__group">
                <button onClick={() => formatText('bold')} className={`rich-text-toolbar__button ${isBold ? 'active' : ''}`} title="מודגש">
                    <strong style={{ fontFamily: 'Arial, sans-serif', fontSize: '14px' }}>B</strong>
                </button>
                <button onClick={() => formatText('italic')} className={`rich-text-toolbar__button ${isItalic ? 'active' : ''}`} title="נטוי">
                    <em style={{ fontFamily: 'Georgia, serif', fontSize: '14px', fontStyle: 'italic' }}>I</em>
                </button>
                <button onClick={() => formatText('underline')} className={`rich-text-toolbar__button ${isUnderline ? 'active' : ''}`} title="קו תחתון">
                    <span style={{ textDecoration: 'underline', fontFamily: 'Arial, sans-serif', fontSize: '14px' }}>U</span>
                </button>
            </div>
            <div className="rich-text-toolbar__separator"></div>
            <div className="rich-text-toolbar__group">
                <button onClick={() => formatAlignment('left')} className="rich-text-toolbar__button" title="יישור לשמאל">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M2 3h12v1H2V3zm0 3h8v1H2V6zm0 3h12v1H2V9zm0 3h8v1H2v-1z" />
                    </svg>
                </button>
                <button onClick={() => formatAlignment('center')} className="rich-text-toolbar__button" title="מרכז">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M2 3h12v1H2V3zm2 3h8v1H4V6zm-2 3h12v1H2V9zm2 3h8v1H4v-1z" />
                    </svg>
                </button>
                <button onClick={() => formatAlignment('right')} className="rich-text-toolbar__button" title="יישור לימין">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M2 3h12v1H2V3zm4 3h8v1H6V6zm-4 3h12v1H2V9zm4 3h8v1H6v-1z" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

// --- הקומפוננטה הראשית ---

export const RichTextEditor = ({ initialHtml = '', onChange, backgroundImage, scale = 1 }) => {
    // טעינת פונטים מה-HTML הראשוני
    useFontLoader(initialHtml);

    const initialConfig = useMemo(() => ({
        namespace: 'RichTextEditor',
        theme: {
            paragraph: 'rich-text-paragraph',
            text: {
                bold: 'rich-text-bold',
                italic: 'rich-text-italic',
                underline: 'rich-text-underline',
            },
        },
        onError: (error) => console.error('Lexical Error:', error),
        nodes: [
            HeadingNode, QuoteNode, ListItemNode, ListNode, LinkNode, AutoLinkNode,
            CustomParagraphNode,
            {
                replace: ParagraphNode,
                with: () => new CustomParagraphNode(),
            },
            ExtendedTextNode,
            {
                replace: TextNode,
                with: (node) => new ExtendedTextNode(node.getTextContent()),
            }
        ]
    }), []);

    const handleEditorChange = (editorState, editor) => {
        editorState.read(() => {
            try {
                const htmlString = $generateHtmlFromNodes(editor, null);
                if (onChange) onChange(htmlString);
            } catch (error) {
                // Ignore errors
            }
        });
    };

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <div className="rich-text-editor" style={{
                // הוספתי רקע לבן כברירת מחדל כדי למנוע מצב של טקסט לבן על רקע שקוף/לבן שנעלם
                backgroundColor: 'white',
                backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}>
                <ToolbarPlugin scale={scale} />
                <div className="rich-text-editor__content">
                    <RichTextPlugin
                        contentEditable={<ContentEditable className="rich-text-editor__input" />}
                        placeholder={<div className="rich-text-editor__placeholder">הכנס טקסט כאן...</div>}
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <OnChangePlugin onChange={handleEditorChange} />
                    <HistoryPlugin />
                    <InitialHTMLPlugin html={initialHtml} />
                </div>
            </div>
        </LexicalComposer>
    );
};